import { useSubscription } from '@/contexts/SubscriptionContext';
import type { LimitName } from '@/types/subscription';

const LIMIT_INFO: Record<LimitName, { label: string; unit: string }> = {
  maxUnits: { label: 'Units', unit: 'units' },
  maxUsers: { label: 'Team Members', unit: 'users' },
  maxWorkOrdersPerMonth: { label: 'Work Orders', unit: 'this month' },
  maxDevices: { label: 'IoT Devices', unit: 'devices' },
  maxProperties: { label: 'Properties', unit: 'properties' },
  storageGB: { label: 'Storage', unit: 'GB' },
};

interface UsageMeterProps {
  limit: LimitName;
  currentValue?: number;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'bar' | 'radial' | 'compact';
}

export function UsageMeter({
  limit,
  currentValue,
  showLabel = true,
  size = 'md',
  variant = 'bar',
}: UsageMeterProps) {
  const { checkLimit } = useSubscription();
  const { current, max, percentage } = checkLimit(limit, currentValue);
  const info = LIMIT_INFO[limit];

  // Color based on percentage
  const getColor = () => {
    if (percentage >= 100) return 'danger';
    if (percentage >= 80) return 'warning';
    return 'primary';
  };

  const color = getColor();

  const colorClasses = {
    primary: {
      bg: 'bg-primary-500',
      track: 'bg-primary-100 dark:bg-primary-500/20',
      text: 'text-primary-600 dark:text-primary-400',
    },
    warning: {
      bg: 'bg-warning-500',
      track: 'bg-warning-100 dark:bg-warning-500/20',
      text: 'text-warning-600 dark:text-warning-400',
    },
    danger: {
      bg: 'bg-danger-500',
      track: 'bg-danger-100 dark:bg-danger-500/20',
      text: 'text-danger-600 dark:text-danger-400',
    },
  };

  const sizeClasses = {
    sm: { height: 'h-1.5', text: 'text-xs' },
    md: { height: 'h-2', text: 'text-sm' },
    lg: { height: 'h-3', text: 'text-base' },
  };

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2">
        <div className={`flex-1 ${sizeClasses[size].height} rounded-full ${colorClasses[color].track}`}>
          <div
            className={`${sizeClasses[size].height} rounded-full ${colorClasses[color].bg} transition-all duration-300`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <span className={`${sizeClasses[size].text} font-medium ${colorClasses[color].text} tabular-nums`}>
          {current}/{max ?? '∞'}
        </span>
      </div>
    );
  }

  if (variant === 'radial') {
    const radius = size === 'lg' ? 40 : size === 'md' ? 32 : 24;
    const strokeWidth = size === 'lg' ? 6 : size === 'md' ? 5 : 4;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (Math.min(percentage, 100) / 100) * circumference;
    const svgSize = (radius + strokeWidth) * 2;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg width={svgSize} height={svgSize} className="transform -rotate-90">
          {/* Background track */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className={colorClasses[color].track.replace('bg-', 'text-').replace('-100', '-200')}
          />
          {/* Progress */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={colorClasses[color].bg.replace('bg-', 'text-')}
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold ${colorClasses[color].text} ${sizeClasses[size].text}`}>
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
    );
  }

  // Default bar variant
  return (
    <div className="space-y-1">
      {showLabel && (
        <div className="flex items-center justify-between">
          <span className={`${sizeClasses[size].text} font-medium text-secondary-700 dark:text-secondary-300`}>
            {info.label}
          </span>
          <span className={`${sizeClasses[size].text} ${colorClasses[color].text} tabular-nums`}>
            {current} / {max ?? '∞'} {info.unit}
          </span>
        </div>
      )}
      <div className={`w-full ${sizeClasses[size].height} rounded-full ${colorClasses[color].track}`}>
        <div
          className={`${sizeClasses[size].height} rounded-full ${colorClasses[color].bg} transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
}

// Usage overview showing all limits
export function UsageOverview() {
  const { usage, plan } = useSubscription();

  const metrics: { limit: LimitName; current: number }[] = [
    { limit: 'maxUnits', current: usage.currentUnits },
    { limit: 'maxUsers', current: usage.currentUsers },
    { limit: 'maxProperties', current: usage.currentProperties },
    { limit: 'maxDevices', current: usage.currentDevices },
    { limit: 'maxWorkOrdersPerMonth', current: usage.workOrdersThisMonth },
    { limit: 'storageGB', current: usage.storageUsedGB },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-secondary-900 dark:text-secondary-100">
          Usage Overview
        </h3>
        <span className="text-sm text-secondary-500 dark:text-secondary-400">
          {plan.name} Plan
        </span>
      </div>
      <div className="space-y-3">
        {metrics.map(({ limit, current }) => (
          <UsageMeter key={limit} limit={limit} currentValue={current} size="sm" />
        ))}
      </div>
    </div>
  );
}
